<script setup>
import { computed, onMounted, ref, onBeforeUnmount } from 'vue'


const isScrolled = ref(false)

const handleScroll = () => {
    isScrolled.value = window.scrollY > 0
}

onMounted(() => {
    window.addEventListener("scroll", handleScroll)
})

onBeforeUnmount(() => {
    window.removeEventListener("scroll", handleScroll)
})

const props = defineProps({
    // Logo source for the navbar brand
    logoSrc: {
        type: String,
        required: true
    },
    // Logo alt text
    logoAlt: {
        type: String,
        default: 'logo'
    },
    // Active tab/menu item
    activePage: {
        type: String,
        default: 'home'
    },
    navItems: {
        type: Array,
        default: () => [
            { id: 'why-bcspro', label: 'WHY BCS PRO', target: 'why-bcspro' },
            { id: 'our-product', label: 'OUR PRODUCT', target: 'our-product' },
            { id: 'testimonials', label: 'TESTIMONIALS', target: 'testimonials' },
            { id: 'founders-note', label: 'FOUNDER\'S NOTES', target: 'founders-note' },
            { id: 'FAQs', label: 'FAQS', target: 'FAQs' }
        ]
    },
    // Action buttons for the navbar
    actionItems: {
        type: Array,
        default: () => [
            { id: 'request-demo', label: 'REQUEST A DEMO', target: 'request-demo', type: 'button', primary: true, showMobile: true },
        ]
    }
})

const emit = defineEmits(['nav-click', 'action-click'])

const handleNavClick = (item) => {
    emit('nav-click', item)
}

const handleActionClick = (item) => {
    emit('action-click', item)
}

const buttonItems = computed(() => {
    return props.actionItems.filter(item => item.type === 'button')
})
</script>

<template>
    <nav :class="{ 'scrolled': isScrolled }" id="main-navbar" style="height: 80px;"
        class="navbar navbar-expand-lg navbar-light bg-dark border-0 py-0 sticky-top z-index-1030">
        <div class="container-fluid menu-desktop">
            <div style="height: 100%;" class="row row-adjustement ps-1">
                <div class="col-10 collapse navbar-collapse px-0" id="navbarSupportedContent"
                    style="margin-left: -0.9rem">
                    <ul class="navbar-nav nav-tabs me-auto h-100 border-0">
                        <li v-for="item in navItems" :key="item.id" class="nav-item bg-dark" role="presentation">
                            <button @click="handleNavClick(item)" class="nav-link text-light ps-3.5 pe-4 text-14 h-100"
                                :class="{
                                    active: activePage === item.id
                                }" :id="`${item.id}-tab`" type="button" role="tab" :aria-controls="item.id"
                                aria-selected="true">
                                <strong>{{ item.label }}</strong>
                            </button>
                        </li>
                    </ul>

                    <div class="d-flex nav-item bg-dark pe-3">
                        <div class="d-flex align-items-center">
                            <a v-for="item in buttonItems" :key="item.id" href="#" :style="{
                                width: item.primary ? '184px' : '92px',
                                marginRight: '12px'
                            }" class="action-button " :class="[
                                item.primary ? 'btn-primary' : 'sign-in-button',
                                'btn'
                            ]" @click.prevent="handleActionClick(item)">
                                {{ item.label }}
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </nav>
</template>

<style scoped lang="scss">
.bg-dark {
    background-color: #2B2B68 !important;
}

.btn-primary {
    background-color: transparent !important;
    border-color: var(--bs-white);
    border-radius: 10px;

    &:hover {
        border: none !important;
    }
}

.sign-in-button {
    background-color: var(--bs-white) !important;
    color: #2B2B68 !important;

    &:hover {
        color: var(--bs-white) !important;
    }
}

.btn-primary:hover,
.sign-in-button:hover {
    background-color: #161634 !important;
}

.hand-cursor {
    cursor: pointer;
}

.row-adjustement {
    width: 100vw;
}

.z-index-1030 {
    z-index: 1030;
}

.nav-link {
    border: none !important;
}

.navbar-nav .nav-link.active {
    background-color: #B9B9D9 !important;
    border: none !important;
    color: #2B2B68 !important;

    strong {
        font-weight: 500 !important;
    }
}

.nav-tabs .nav-link:hover:not(.active) {
    background-color: #B9B9D9;
    color: #2B2B68 !important;
    border-color: #161634 !important;
}

.navbar-nav .nav-link {
    border-top: 5px transparent;
    height: 4.55vh;
}

.nav-tabs {
    --bs-nav-tabs-border-radius: 0;
}

.menu-desktop {
    height: 100%;
}

.menu-desktop,
.logo-desktop {
    display: block;
}

.navbar {
    transition: height 0.3s ease, background-color 0.3s ease;

    ul {
        justify-content: end !important;
        width: 100%;
        padding-right: 65px;

    }
}

nav.scrolled {
    height: 51px !important;
    transition: height 0.3s ease;
}

.navbar .action-button {
    opacity: 0;
    transform: translate3d(0, 30px, 0);
    animation: navButtonFadeUp 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
    transform-style: preserve-3d;
    will-change: opacity, transform;
}

@keyframes navButtonFadeUp {
    0% {
        opacity: 0;
        transform: translate3d(0, 30px, 0);
    }

    100% {
        opacity: 1;
        transform: translate3d(0, 0, 0);
    }
}

.navbar .action-button {
    display: flex;
    align-items: center;
    justify-content: center;
    text-decoration: none;
    transition: all 0.3s ease;
}

.navbar .action-button:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

nav.scrolled .action-button {
    opacity: 1;
    transform: translate3d(0, 0, 0);
}

@media (max-width: 1030px) {
    nav.navbar {
        display: none !important;
    }
}
</style>